import React, { useMemo, useState } from 'react';

import { Box } from '@opendoor/bricks/core';

import { FinalizedOffer, UnderContractOffer } from 'sellDirect/OfferContext';
import { formatCurrency } from 'shared/helpers/formatters';
import { DocumentsSection } from 'sellDirect/components/closingDash/DocumentsSection';
import {
  DesktopThumbnailBorderRadius,
  DesktopThumbnailWidth,
  MobileThumbnailBorderRadius,
  MobileThumbnailWidth,
  OverviewHeader,
} from 'shared/components/closingDash/OverviewHeader';
import { SupportSection } from 'sellDirect/components/closingDash/SupportSection';
import { FinalOfferModal } from 'sellDirect/components/closingDash/finalOffer/FinalOfferModal';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { radii } from '@opendoor/bricks/theme/eero';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { getHubHowItWorksSellRoute, getHubLateCheckoutWizardRoute } from 'shared/helpers/routes';
import { OptionalClosingTasksSection } from 'sellDirect/components/closingDash/OptionalClosingTasksSection';
import { ClosingTasksSection } from 'sellDirect/components/closingDash/ClosingTasksSection';
import {
  ClosingFAQs,
  getInContractAgentFaqs,
  getInContractFaqs,
} from 'sellDirect/components/closingDash/ClosingFAQs';
import { SubtitleItemProps } from 'shared/components/closingDash/OverviewHeaderSubtitle';
import { useSellDirectTasks } from 'sellDirect/helpers/useSellDirectTasks';
import { CompsMap } from 'sellDirect/components/comps/CompsMap';
import { getOverviewHeaderSteps } from 'sellDirect/helpers/closingDash/getOverviewHeaderStepItems';
import { JourneyCard } from 'shared/components/JourneyCard';
import { SellDirectLayout } from 'sellDirect/components/offer/SellDirectLayout';
import { FULL_DATE_FORMAT, SHORTER_FULL_DATE_FORMAT } from 'shared/helpers/datetime';
import { MapboxEvent } from 'react-map-gl';

export interface UnderContractOfferSellPageProps {
  isLoading: boolean;
  offer: UnderContractOffer | FinalizedOffer;
  closingTasks: ClosingTasks;
}

/**
 * Shown when under contract.
 *
 * Usually, this is only shown when offer.state === 'UnderContract'.
 * However, there's a special case when offer.state === 'Finalized' && offer.isPurchaseAgreementSigned.
 *
 * @throws Error if offer in illegal state
 */
export const UnderContractOfferPage: React.FC<UnderContractOfferSellPageProps> = ({
  isLoading,
  offer,
  closingTasks,
}) => {
  if (offer.state === 'Finalized' && !offer.isPurchaseAgreementSigned) {
    throw new Error(
      'cannot show UnderContractOfferPage when offer is finalized and purchase agreement is not signed',
    );
  }

  const { isMobile } = useDevice();
  const { tasks, loading: loadingTasks } = useSellDirectTasks();

  const [finalOfferModalOpen, setFinalOfferModalOpen] = useState(false);
  const overviewSubtitleItems: SubtitleItemProps[] = [
    {
      type: 'button',
      onClick: () => setFinalOfferModalOpen(true),
      ariaLabel: '',
      text: 'Offer price',
      subtext: formatCurrency(offer.underwriting.finalizedHeadlinePriceCents / 100),
    },
    {
      type: 'non-interactive',
      text: 'Close date',
      subtext: offer.closeEscrowDate?.format(FULL_DATE_FORMAT) ?? '',
    },
  ];

  // Only show the late checkout date if it exists and it's complete
  if (closingTasks.lateCheckout?.complete && closingTasks.lateCheckout?.taskable.lateCheckoutDate) {
    const canRequestLCO = offer.state === 'UnderContract' && offer.canScheduleLateCheckout;
    const subtext =
      closingTasks.lateCheckout.taskable.lateCheckoutDate.format(SHORTER_FULL_DATE_FORMAT);
    overviewSubtitleItems.push(
      canRequestLCO
        ? {
            text: 'Late checkout',
            subtext,
            // If the user cannot schedule a late checkout anymore, don't make it a link
            href: getHubLateCheckoutWizardRoute(),
            type: 'internal-link',
            ariaLabel: '',
          }
        : {
            type: 'non-interactive',
            text: 'Late checkout',
            subtext,
          },
    );
  }

  const { overviewHeaderSteps, currentStep } = useMemo(
    () => getOverviewHeaderSteps(tasks),
    [tasks],
  );

  const onMapLoad = (event: MapboxEvent) => {
    // Due to the limitations of the React Map Gl library, we can't specify the border-radius
    // style on the container element. (There is no prop to pass in that targets that container).
    // So instead, we need to use this somewhat hacky callback function to manually
    // set the style of the container, to apply the correct border radius.
    const container = event.target.getContainer();
    const oldStyle = container.getAttribute('style');
    let newStyle = '';
    if (isMobile) {
      newStyle = `border-radius: ${radii[MobileThumbnailBorderRadius]};`;
    } else {
      newStyle = `border-top-left-radius: ${radii[DesktopThumbnailBorderRadius]}; border-bottom-left-radius: ${radii[DesktopThumbnailBorderRadius]};`;
    }
    container.setAttribute('style', `${oldStyle} ${newStyle}`);

    // We also want to adjust the z-index of the bottom left & right icons so they dont go over any other elements on the screen
    const bottomLeftIcon = container.querySelector('.mapboxgl-ctrl-bottom-left');
    const bottomRightIcon = container.querySelector('.mapboxgl-ctrl-bottom-right');
    bottomLeftIcon?.setAttribute('style', 'z-index: 0');
    bottomRightIcon?.setAttribute('style', 'z-index: 0');
  };

  const OverviewHeaderPhotoComponent =
    !isMobile && offer.address.latitude && offer.address.longitude ? (
      <CompsMap
        width={[MobileThumbnailWidth, null, DesktopThumbnailWidth]}
        height="100%"
        comps={[]}
        subject={{
          latitude: offer.address.latitude,
          longitude: offer.address.longitude,
        }}
        onClickMarker={() => {}}
        onMapLoad={onMapLoad}
        freezeMap
      />
    ) : null;

  const inContractFaqs = offer.shouldSeeAgentExperience
    ? getInContractAgentFaqs()
    : getInContractFaqs();

  return (
    <>
      <SellDirectLayout isLoading={isLoading} address={offer.address}>
        <OverviewHeader
          title={
            offer.shouldSeeAgentExperience
              ? `Your client's home is almost sold!`
              : `You're selling your home!`
          }
          currentStepName={currentStep.name}
          photo={OverviewHeaderPhotoComponent}
          alt=""
          overviewSubtitleItems={overviewSubtitleItems}
          steps={tasks ? overviewHeaderSteps : []}
        />
        {offer.state === 'Finalized' ? (
          <>
            <ClosingTasksSection tasks={[]} loading={true} mt="10" />
          </>
        ) : (
          <>
            <ClosingTasksSection tasks={tasks} loading={loadingTasks} mt="10" />
            <OptionalClosingTasksSection offer={offer} mt="10" />
          </>
        )}
        <ClosingFAQs faqs={inContractFaqs} mt="10" />
        <SupportSection contactTypes={['experiencePartner', 'titleAndEscrow']} />
        <DocumentsSection offer={offer} closingTasks={closingTasks} />

        <Box width="100%" mt={9}>
          <JourneyCard
            message="How selling works"
            showCTA={false}
            url={getHubHowItWorksSellRoute()}
          />
        </Box>
      </SellDirectLayout>
      <FinalOfferModal
        offer={offer}
        closingTasks={closingTasks}
        isOpen={finalOfferModalOpen}
        onRequestClose={() => setFinalOfferModalOpen(false)}
      />
    </>
  );
};
